import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import DateConverter from "../../utils/DateConverter";
import {
  PrimaryColor,
  TableBodyGrey,
  TableHeaderGrey,
  TableRowHover,
} from "../../shared/styles/colors";
import { Spinner, ClientModal } from "../index";
import { Card, CardContent, Typography, Button } from "@material-ui/core";
import { TablePagination } from "@mui/material";
import { ToastContainer } from "react-toastify";
import Client from "../../model/client";
import DatabaseEntities from "../../model/enum/DatabaseEntities";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ApiService from "../../service/ApiService";
import ClientDetailModal from "./ClientDetailModal";
import CachedIcon from "@mui/icons-material/Cached";
import Pathnames from "../../model/enum/Pathnames";
import PageResultResponse from "../../model/pageResultResponse";
import useStyles from "../styles/cardStyles";
import Misc from "../../utils/Misc";

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: TableHeaderGrey,
    color: PrimaryColor,
    fontWeight: 700,
    whiteSpace: "nowrap",
    zIndex: 0,
    position: "static",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    color: TableBodyGrey,
    fontFamily: "monospace",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: TableRowHover,
  },
}));

const Clients = () => {
  const classes = useStyles();
  const [clients, setClients] = useState<Client[]>([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set("page", page.toString());
    url.searchParams.set("rowsPerPage", rowsPerPage.toString());
    window.history.pushState({}, Pathnames.CLIENTS_LIST, url);
  });

  //Get all the clients from the database
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      searchParams.set("page", page.toString());
      searchParams.set("rowsPerPage", rowsPerPage.toString());

      await ApiService.get<PageResultResponse>(
        `${DatabaseEntities.CLIENTS}?${searchParams.toString()}`
      ).then((data: PageResultResponse) => {
        if (Misc.isArrayEmpty(data.items)) return;
        const { items, count } = data;
        setClients(items);
        setTotalPages(count);
      });
      setLoading(false);
    };

    if (loading && Misc.isArrayEmpty(clients)) fetchData();
  }, [clients]);

  const resetList = (): void => {
    setPage(1);
    setRowsPerPage(10);
    setClients([]);
    setLoading(true);
    if (searchParams.has("clientId")) {
      searchParams.delete("clientId");
      setSearchParams(searchParams);
    }
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown> | null,
    pg: number
  ) => {
    setPage(pg + 1);
    scrollToTop();
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    let row = parseInt(event.target.value);
    setRowsPerPage(row);
    scrollToTop();
  };

  const scrollToTop = () => {
    setClients([]);
    setLoading(true);
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  const addIdToQueryPath = (id?: number) => {
    if (!id) return;

    const url = new URL(window.location.href);
    url.searchParams.set("clientId", id.toString());
    window.history.pushState({}, Pathnames.APPOINTMENTS_LIST, url);
  };

  const removeIdFromQueryPath = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("clientId");
    window.history.replaceState({}, "", url.pathname + url.search);
  };

  return (
    <>
      <Card raised className={classes.card}>
        <div className={classes.headerContainer}>
          <Typography className={classes.title}>Clients</Typography>
          <div className={classes.headerDiv}>
            <ClientModal resetList={resetList} />
            <Button onClick={resetList}>
              <CachedIcon />
            </Button>
          </div>
        </div>
        <CardContent>
          <Paper>
            <TableContainer>
              <Table
                stickyHeader
                aria-label="customized table"
                padding="normal"
                size="medium"
              >
                <TableHead>
                  <TableRow>
                    <StyledTableCell align="center">
                      <MoreVertIcon />
                    </StyledTableCell>
                    <StyledTableCell align="right">Id</StyledTableCell>
                    <StyledTableCell align="right">Name</StyledTableCell>
                    <StyledTableCell align="right">Phone</StyledTableCell>
                    <StyledTableCell align="right">Email</StyledTableCell>
                    <StyledTableCell align="right">Address</StyledTableCell>
                    <StyledTableCell align="right">State</StyledTableCell>
                    <StyledTableCell align="right">
                      Service Added
                    </StyledTableCell>
                    <StyledTableCell align="right">Created At</StyledTableCell>
                    <StyledTableCell align="right">Updated At</StyledTableCell>
                  </TableRow>
                </TableHead>
                {loading ? (
                  <Spinner />
                ) : (
                  <TableBody>
                    {clients.length > 0 ? (
                      clients.map((row: Client) => (
                        <StyledTableRow key={row.id}>
                          <StyledTableCell align="center">
                            <ClientDetailModal
                              client={row}
                              resetList={resetList}
                              onOpen={() => {
                                addIdToQueryPath(row?.id);
                              }}
                              onClose={() => {
                                removeIdFromQueryPath();
                              }}
                            />
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            {row.id}
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            {row.firstName} {row.lastName}
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            {row.phone && Misc.formatPhoneNumber(row.phone)}
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            {row.email}
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            {row.street || row.state || row.city ? (
                              <>
                                {row.street ? `${row.street.trim()}, ` : ""}
                                {`${row.city} ${row.state} ${row.zip}`}
                              </>
                            ) : (
                              <Typography>--</Typography>
                            )}
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            {row.state ? (
                              <>{row.state}</>
                            ) : (
                              <Typography>--</Typography>
                            )}
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            {row.service}
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            {DateConverter.toFullDateAndHours(row.createdAt)}
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            {DateConverter.toFullDateAndHours(row.updatedAt)}
                          </StyledTableCell>
                        </StyledTableRow>
                      ))
                    ) : (
                      <StyledTableCell align="right">No items</StyledTableCell>
                    )}
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Paper>
          <TablePagination
            component="div"
            count={totalPages}
            page={page - 1}
            rowsPerPage={rowsPerPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>
      <ToastContainer />
    </>
  );
};

export default Clients;
